"use client";
import useIsMobile from "@/hooks/useMobile";
import UserImg from "@/public/b2b.png";
import BellIcon from "@/public/icons/BellIcon";
import InfoIcon from "@/public/icons/InfoIcon";
import LogOut from "@/public/icons/LogOut";
import PitchesIcon from "@/public/icons/PitchesIcon";
import myDashboard from "@/public/mydashboard.png";
import teamSetting from "@/public/teamsetting.png";
import profile from "@/public/profile.png";
import teamdashboard from "@/public/teamdashboard.png";
import TeamDashboardIcon from "@/public/icons/teamDashboardIcon.png";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { setIsSidebarOpen } from "@/state";
import {
  useCreatePortalSessionMutation,
  useLogoutMutation,
} from "@/state/api";
import { CreditCard, Sparkles } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

const menu = [
  {
    label: "All Pitches",
    icon: <PitchesIcon />,
    href: "/all-pitches",
    sub: [],
  },
  // {
  //   label: "Past 7 days",
  //   icon: <MessageIcon />,
  //   href: "/all-pitches",
  //   sub: ["Finance x Manager", "Finance x CEO", "Finance x Manager", "+5 more"],
  // },
  // {
  //   label: "Past 30 days",
  //   icon: <MessageIcon />,
  //   sub: [
  //     "Finance x Manager",
  //     "Finance x CEO",
  //     "Finance x Manager",
  //     "View all",
  //   ],
  //   href: "/all-pitches",
  // },
];
const TeamMenu = [
  {
    label: "My Dashboard",
    icon: <Image src={myDashboard} alt="Dashboard" width={18} height={18} />,
    href: "/",
    sub: [],
  },
  {
    label: "Team Dashboard",
    icon: (
      <Image src={teamdashboard} alt="Team Settings" width={18} height={18} />
    ),
    href: "/team-dashboard",
    sub: [],
  },

  {
    label: "Team Settings",
    icon: (
      <Image src={teamSetting} alt="Team Settings" width={18} height={18} />
    ),
    href: "/team-settings",
    sub: [],
  },
  {
    label: "My Profile",
    icon: <Image src={profile} alt="Team Settings" width={18} height={18} />,
    href: "/my-profile",
    sub: [],
  },
  {
    label: "All Pitches",
    icon: <PitchesIcon />,
    href: "/all-pitches",
    sub: [],
  },
];

const DashboardSidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const router = useRouter();
  const dispatch = useDispatch();
  const isSidebarOpen = useSelector(
    (state: any) => state.global?.sideBar?.isSidebarOpen ?? false,
  );
  const user = useSelector((state: any) => state.global.user);
  const subscription = user.subscription;
  let isActiveTeamSubscription = false;
  if (subscription) {
    const now = new Date();
    const startDate = new Date(subscription.startDate);
    const endDate = new Date(subscription.endDate);
    if (
      subscription.ownerId === user.id &&
      subscription.planType === "TEAM" &&
      startDate <= now &&
      endDate >= now
    ) {
      isActiveTeamSubscription = true;
    }
  } else {
    isActiveTeamSubscription = false;
  }
  const MenuToUse = isActiveTeamSubscription ? TeamMenu : menu;

  // Payments off and no access right now - i.e. a trial that has run out (a
  // brand-new account is auto-granted its trial by /me and never sees this).
  const trial = user?.trial;
  const canStartTrial = !!trial?.canStartTrial;
  const trialDays = trial?.trialDays || 7;
  // The user chooses between a single-user and a team trial on /plans.
  const handleStartFreeTrial = () => {
    router.push("/plans");
  };

  // Only the owner of a plan paid through Stripe has a customer portal to open -
  // trials, free grants and team seat members don't.
  const hasStripeSubscription =
    subscription?.ownerId === user.id &&
    subscription?.paymentProvider === "stripe";
  // Everyone without a Stripe plan (trial, expired/no plan, team seat member)
  // gets an info popup instead of the portal.
  const isOnTrial = !!trial?.isTrial;
  const hasActivePlan =
    !!subscription && new Date(subscription.endDate) >= new Date();
  const stripeEnabled = useSelector(
    (state: any) => state.global.config?.stripeEnabled ?? true,
  );
  const [isPlanInfoOpen, setIsPlanInfoOpen] = useState(false);
  const trialEndDate = trial?.endDate || subscription?.endDate;
  const formattedTrialEndDate = trialEndDate
    ? new Date(trialEndDate).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;
  // No access: explain what ended - a trial or a paid plan, and whether it was
  // the user's own or their team's (a member can only ask the owner to renew).
  const expiredPlan = user?.expiredPlan;
  const expiredOn = expiredPlan?.endDate
    ? ` on ${new Date(expiredPlan.endDate).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })}`
    : "";
  const nextStep = stripeEnabled
    ? "Please purchase a new plan to continue."
    : "You can start a new free trial to continue.";
  let expiredTitle = "No active plan";
  let expiredDescription = stripeEnabled
    ? "You don't have an active plan. Please purchase a new plan to continue."
    : "You don't have an active plan right now.";
  if (expiredPlan?.isTeamMember) {
    const contact = expiredPlan.ownerEmail
      ? `your team owner (${expiredPlan.ownerEmail})`
      : "your team owner";
    expiredTitle = expiredPlan.isTrial
      ? "Your team's free trial has ended"
      : "Your team's plan has expired";
    expiredDescription = `Your team's ${
      expiredPlan.isTrial ? "free trial ended" : "plan expired"
    }${expiredOn}. Please contact ${contact} to renew access, or ${
      stripeEnabled ? "purchase your own plan" : "start your own free trial"
    }.`;
  } else if (expiredPlan) {
    expiredTitle = expiredPlan.isTrial
      ? "Your free trial has ended"
      : "Your plan has expired";
    expiredDescription = `Your ${
      expiredPlan.isTrial
        ? `${expiredPlan.planType === "TEAM" ? "team " : ""}free trial ended`
        : "plan expired"
    }${expiredOn}. ${nextStep}`;
  }

  const [createPortalSession, { isLoading: isOpeningPortal }] =
    useCreatePortalSessionMutation();

  const handleManageSubscription = async () => {
    if (!hasStripeSubscription || !hasActivePlan) {
      setIsPlanInfoOpen(true);
      return;
    }

    const portalWindow = window.open("", "_blank");

    if (!portalWindow) {
      toast.error("Please allow pop-ups to manage your subscription", {
        position: "top-right",
        closeButton: true,
      });
      return;
    }

    portalWindow.document.title = "Opening subscription management...";
    portalWindow.document.body.innerHTML = `
      <main style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f8fafc;font-family:Arial,sans-serif;color:#1b2a36">
        <div style="display:flex;flex-direction:column;align-items:center;gap:16px;text-align:center;padding:24px">
          <div aria-hidden="true" style="width:36px;height:36px;border:4px solid #dbeafe;border-top-color:#2da9df;border-radius:50%;animation:spin .8s linear infinite"></div>
          <div>
            <h1 style="font-size:20px;margin:0 0 8px">Opening subscription management</h1>
            <p style="font-size:14px;color:#64748b;margin:0">Please wait while we connect you securely...</p>
          </div>
        </div>
        <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
      </main>
    `;
    portalWindow.opener = null;

    try {
      const result = await createPortalSession().unwrap();
      if (result?.data?.url) {
        portalWindow.location.href = result.data.url;
      } else {
        portalWindow.close();
      }
    } catch (error: any) {
      portalWindow.close();
      console.log("failed to open billing portal:", error);
      toast.error(
        error?.data?.message || "Unable to open subscription management",
        { position: "top-right", closeButton: true },
      );
    }
  };

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isSidebarOpen]);

  const personName = useSelector((state: any) => state.global.user);
  const name =
    personName.role === "user"
      ? personName?.aboutYou?.name
      : personName?.aboutCompany?.name;
  const [
    logout,
    { data: logoutData, isLoading: logoutLoading, isError: isErrorLogin },
  ] = useLogoutMutation();

  const handleClick = async () => {
    try {
      const result = await logout({}).unwrap();
      localStorage.clear();
      toast.success("Logout successfully!", {
        style: { background: "#2da9df", color: "#fff" },
        duration: 5000,
        closeButton: true,
        position: "top-right",
        cancel: true,
      });
      setTimeout(() => {
        window.location.href = "/sign-in";
      }, 2000);
    } catch (error) {
      console.log("logout failed:", error);
    }
  };

  const handleSideBar = () => {
    dispatch(
      setIsSidebarOpen({
        isSidebarOpen: !isSidebarOpen,
      }),
    );
  };

  return (
    <>
      {/* Mobile sidebar overlay */}
      <div
        className={`fixed inset-0 z-50 bg-black/40 xl:hidden transition-opacity duration-300 ${
          isSidebarOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      />

      <div
        className={`${isSidebarOpen ? "w-4/5 fixed z-50 px-8" : "w-0"} lg:flex
            ${collapsed ? "lg:w-20" : "lg:w-72"} w-0 overflow-hidden md:px-8 pt-11 pb-5 h-full transition-all duration-300 rounded-r-[40px] min-h-screen bg-gradient-to-b from-[#1b2a36] to-[#2c93c1] text-white flex flex-col justify-between`}
      >
        <div>
          {!collapsed && (
            <div className="flex justify-between items-center mb-6">
              {/* <button className="bg-[#2DA9DF] text-xs px-4 py-2 rounded-full flex gap-3 items-center"> */}
              {/* <VictoryIcon /> */}
              {/* <span>Pro Plan</span> */}
              {/* </button> */}
              <div className="flex gap-4 ml-auto">
                {/* <SettingIcon className="text-lg" /> */}
                <div
                  className="relative cursor-pointer"
                  onClick={() => router.push("/notifications")}
                >
                  <BellIcon className="text-lg" />
                  {/* Notification count badge */}
                  {user.notifications > 0 && (
                    <div className="absolute -top-3 -right-3 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                      {user.notifications}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* User Image */}
          <div
            className="flex items-center gap-3 mb-3 cursor-pointer"
            onClick={() => setCollapsed(!collapsed)}
          >
            <div className="relative ">
              <Image
                src={UserImg}
                alt="User"
                width={35}
                height={35}
                className="rounded-full w-full h-full object-cover"
              />
            </div>
            {!isMobile ? (
              !collapsed && (
                <p className="font-normal text-xl">
                  {name ? name.charAt(0).toUpperCase() + name.slice(1) : ""}
                </p>
              )
            ) : (
              <p className="font-normal text-xl">
                {name ? name.charAt(0).toUpperCase() + name.slice(1) : ""}
              </p>
            )}
            {/* Close button on mobile */}
            <div className="ms-auto flex justify-end xl:hidden px-4">
              <button onClick={handleSideBar} className="text-white text-2xl">
                &times;
              </button>
            </div>
          </div>

          {canStartTrial && (
            <button
              onClick={handleStartFreeTrial}
              title={`Get ${trialDays} days of free access`}
              className={`flex items-center justify-center gap-2 bg-[#2DA9DF] hover:bg-[#2596c7] disabled:opacity-70 text-white font-medium rounded-full mb-4 cursor-pointer transition-colors ${
                !isMobile && collapsed ? "w-10 h-10 p-0" : "w-full px-4 py-2"
              }`}
            >
              <Sparkles className="w-4 h-4 shrink-0" />
              {(isMobile || !collapsed) && (
                <span className="text-sm">Get free trial</span>
              )}
            </button>
          )}

          <hr className="border-[#ffffff99] mb-8" />

          <nav className="flex flex-col gap-2">
            {/* Dashboard */}
            {/* <ul>
              <li
                className={`flex items-center gap-3  rounded-md bg-[#00000033] ${collapsed ? "p-0" : "py-2 px-3"}`}
              >
                <DashboardIcon />
                {!collapsed && <span>My Dashboard</span>}
              </li>
            </ul> */}
            {/* <hr className="border-[#ffffff99] mt-3 mb-4" /> */}

            <ul className="mt-3 flex flex-col gap-5">
              {MenuToUse.map((item) => (
                <li key={item.label} onClick={() => router.push(item.href)}>
                  <Link
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      if (item.sub.length > 0) {
                        setActiveLabel((prev) =>
                          prev === item.label ? null : item.label,
                        );
                      }
                    }}
                    className="flex items-center gap-2"
                  >
                    {item.icon}
                    {!isMobile ? (
                      !collapsed && (
                        <span className="font-medium">{item.label}</span>
                      )
                    ) : (
                      <span className="font-medium">{item.label}</span>
                    )}
                  </Link>

                  {!collapsed &&
                    activeLabel === item.label &&
                    item.sub.length > 0 &&
                    item.label !== "All Pitches" && (
                      <ul className="mt-3 flex flex-col gap-3 font-normal mb-4">
                        {item.sub.map((subItem, idx) => (
                          <li
                            key={idx}
                            className={
                              idx === item.sub.length - 1
                                ? "cursor-pointer underline underline-offset-1"
                                : ""
                            }
                          >
                            {subItem}
                          </li>
                        ))}
                      </ul>
                    )}
                </li>
              ))}
              <li
                className="flex items-center gap-2 cursor-pointer"
                onClick={!isOpeningPortal ? handleManageSubscription : () => {}}
              >
                {isOpeningPortal ? (
                  <div className="w-[18px] h-[18px] border-2 border-white border-t-transparent rounded-full animate-spin shrink-0"></div>
                ) : (
                  <CreditCard size={18} className="shrink-0" />
                )}
                {!isMobile ? (
                  !collapsed && (
                    <span className="font-medium">Manage Subscription</span>
                  )
                ) : (
                  <span className="font-medium">Manage Subscription</span>
                )}
              </li>
            </ul>
          </nav>
        </div>

        <div className="mb-[4.5rem] lg:mb-0 mt-auto">
          <div
            className="flex items-center gap-3 mb-2 cursor-pointer"
            onClick={() => router.push("/contact-us")}
          >
            <InfoIcon className="text-lg" />
            {!isMobile ? (
              !collapsed && <span className="cursor-pointer">Contact us</span>
            ) : (
              <span className="cursor-pointer">Contact us</span>
            )}
          </div>
          <hr className="border-white/20 mb-2" />
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={!logoutLoading ? handleClick : () => {}}
          >
            {logoutLoading ? (
              <div className="flex gap-3 items-center ">
                <div className="w-6 h-6 border-4 border-grey-100 border-t-transparent rounded-full animate-spin"></div>
                {/* <LogOut className="text-lg" /> */}
              </div>
            ) : (
              <LogOut className="text-lg" />
            )}

            {/* {!collapsed && <span>Logout</span>} */}
          </div>
        </div>
      </div>

      <AlertDialog open={isPlanInfoOpen} onOpenChange={setIsPlanInfoOpen}>
        <AlertDialogContent>
          {!hasActivePlan ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{expiredTitle}</AlertDialogTitle>
                <AlertDialogDescription>{expiredDescription}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                {stripeEnabled ? (
                  <>
                    <AlertDialogCancel>Close</AlertDialogCancel>
                    <AlertDialogAction onClick={() => router.push("/plans")}>
                      View plans
                    </AlertDialogAction>
                  </>
                ) : canStartTrial ? (
                  <>
                    <AlertDialogCancel>Close</AlertDialogCancel>
                    <AlertDialogAction onClick={handleStartFreeTrial}>
                      Start free trial
                    </AlertDialogAction>
                  </>
                ) : (
                  <AlertDialogAction>Got it</AlertDialogAction>
                )}
              </AlertDialogFooter>
            </>
          ) : isOnTrial ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Your free trial is running</AlertDialogTitle>
                <AlertDialogDescription>
                  {formattedTrialEndDate
                    ? `Your free trial will end on ${formattedTrialEndDate}.`
                    : "You are currently on a free trial."}
                  {trial?.daysLeft > 0 &&
                    ` You have ${trial.daysLeft} ${
                      trial.daysLeft === 1 ? "day" : "days"
                    } left.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction>Got it</AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Plan managed by your team</AlertDialogTitle>
                <AlertDialogDescription>
                  Your plan is managed by your team owner. Please contact them
                  for any billing changes.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction>Got it</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DashboardSidebar;
